import { useContext, useMemo } from 'react'
import { useAgenciesList } from '~/api/queries/agency'
import { ApplicantsChipValue } from '~/components/Filters/FilterBar/chips/applicant'
import { ContactsChipValue } from '~/components/Filters/FilterBar/chips/contact'
import { UsersChipValue } from '~/components/Filters/FilterBar/chips/users'
import { PersonsFilterEditor } from '~/components/Filters/FilterBar/editors/PersonsFilterEditor'
import type { FilterDefinition } from '~/components/Filters/FilterBar/types'
import {
  type TaskView,
  taskLinkedToLabels,
  taskStatusLabels
} from '~/domains/tasks/types'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'
import {
  canAccessApplicants,
  canAccessContacts
} from '~/domains/users/utils/groups'
import { AuthContext } from '~/utils/contextes'
import { splitList } from '~/utils/lists'

const usersFilter = (
  key: string,
  label: string,
  placeholder: string
): FilterDefinition => ({
  key,
  label,
  group: 'Utilisateur',
  type: 'autocomplete',
  renderEditor: ({ value, onChange }) => (
    <UsersAutocomplete
      key={value || 'empty'}
      inlineList
      multiple
      ids={splitList(value)}
      small
      fullWidth
      placeholder={placeholder}
      emptyLabel="Tous"
      onChange={(nextValues) => {
        if (nextValues.includes('')) {
          onChange('')
          return
        }
        onChange(nextValues.filter(Boolean).join(','))
      }}
    />
  ),
  renderValue: (value) => <UsersChipValue value={value} />
})

export const useTaskFilterDefinitions = (
  view: TaskView = 'todo'
): FilterDefinition[] => {
  const { data: actionTypes } = useTaxonomy('action_types')
  const { data: agencies } = useAgenciesList()
  const currentUser = useContext(AuthContext)
  const withApplicants = canAccessApplicants(currentUser)
  const withContacts = canAccessContacts(currentUser)

  return useMemo(
    () => [
      // Treated tasks have no overdue notion: the status filter only makes sense on open ones
      ...(view === 'todo'
        ? [
            {
              key: 'status',
              label: 'Statut',
              type: 'select',
              options: [{ value: 'overdue', label: taskStatusLabels.overdue }],
              emptyLabel: 'Toutes'
            } satisfies FilterDefinition
          ]
        : []),
      usersFilter('user', 'Assigné à', 'Rechercher des utilisateurs'),
      {
        key: 'agency',
        label: 'Agence',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options:
          agencies?.map((agency) => ({
            label: agency.name,
            value: String(agency.id)
          })) ?? [],
        emptyLabel: 'Toutes'
      },
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options: [
          { value: 'call', label: 'Appel' },
          { value: 'meeting', label: 'Rendez-vous' },
          { value: 'task', label: 'Tâche libre' },
          ...(actionTypes ?? [])
            .filter((type) => type.expectsCompletion)
            .map((type) => ({ value: String(type.id), label: type.name }))
        ],
        emptyLabel: 'Tous'
      },
      {
        key: 'linkedTo',
        label: 'Liée à',
        type: 'select',
        multiSelect: true,
        options: Object.entries(taskLinkedToLabels)
          .filter(
            ([value]) =>
              (value !== 'applicant' || withApplicants) &&
              (value !== 'contact' || withContacts)
          )
          .map(([value, label]) => ({ value, label })),
        emptyLabel: 'Tous'
      },
      ...(withApplicants
        ? [
            {
              key: 'applicants',
              label: 'Candidat',
              type: 'autocomplete',
              renderEditor: ({ value, onChange, onClose }) => (
                <PersonsFilterEditor
                  key={value || 'empty'}
                  type="applicant"
                  value={value}
                  placeholder="Rechercher un candidat"
                  onChange={(next) => {
                    onChange(next)
                    onClose()
                  }}
                />
              ),
              renderValue: (value) => <ApplicantsChipValue value={value} />
            } satisfies FilterDefinition
          ]
        : []),
      ...(withContacts
        ? [
            {
              key: 'contacts',
              label: 'Contact',
              type: 'autocomplete',
              renderEditor: ({ value, onChange, onClose }) => (
                <PersonsFilterEditor
                  key={value || 'empty'}
                  type="contact"
                  value={value}
                  placeholder="Rechercher un contact"
                  onChange={(next) => {
                    onChange(next)
                    onClose()
                  }}
                />
              ),
              renderValue: (value) => <ContactsChipValue value={value} />
            } satisfies FilterDefinition
          ]
        : []),
      usersFilter('createdBy', 'Créée par', 'Rechercher des utilisateurs'),
      // An open task has no completer yet: the filter only makes sense on treated ones
      ...(view === 'done'
        ? [
            usersFilter(
              'completedBy',
              'Terminée par',
              'Rechercher des utilisateurs'
            )
          ]
        : []),
      {
        label: 'Échéance',
        menuLabel: "Date d'échéance",
        type: 'dateRange',
        keyAfter: 'dueAfter',
        keyBefore: 'dueBefore'
      }
    ],
    [actionTypes, agencies, view, withApplicants, withContacts]
  )
}
